import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@clerk/react';
import { api } from '../api/client';
import type { Post, PostSummary, CreatePostInput } from '../api/types';

interface AdminPageProps {
  isAdmin: boolean;
  adminLoading: boolean;
  onNavigateHome: () => void;
}

type EditorMode = { type: 'none' } | { type: 'new' } | { type: 'edit'; post: Post };

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY_FORM: CreatePostInput = {
  title: '',
  content: '',
  kind: 'article',
  date: TODAY,
  author: 'James Steele',
  summary: '',
  published: false,
};

const AdminPage: React.FC<AdminPageProps> = ({ isAdmin, adminLoading, onNavigateHome }) => {
  const { getToken } = useAuth();

  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [editor, setEditor] = useState<EditorMode>({ type: 'none' });
  const [form, setForm] = useState<CreatePostInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    const token = await getToken({ template: 'baldydev' });
    setListLoading(true);
    api.posts
      .list(token)
      .then(setPosts)
      .catch(console.error)
      .finally(() => setListLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) fetchPosts();
  }, [isAdmin, fetchPosts]);

  const openNew = () => {
    setForm({ ...EMPTY_FORM, date: new Date().toISOString().slice(0, 10) });
    setEditor({ type: 'new' });
    setSaveError(null);
    setPreview(false);
  };

  const openEdit = async (slug: string) => {
    const token = await getToken({ template: 'baldydev' });
    try {
      const post = await api.posts.get(slug, token);
      setForm({
        title: post.title,
        content: post.content,
        kind: post.kind,
        date: post.date || post.createdAt.slice(0, 10),
        author: post.author || '',
        summary: post.summary || '',
        published: post.published,
      });
      setEditor({ type: 'edit', post });
      setSaveError(null);
      setPreview(false);
    } catch (err) {
      console.error('Failed to load post for editing', err);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setSaveError('Title is required');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken({ template: 'baldydev' });
      if (!token) throw new Error('Not authenticated');
      if (editor.type === 'new') {
        await api.posts.create(form, token);
      } else if (editor.type === 'edit') {
        await api.posts.update(editor.post.slug, form, token);
      }
      setEditor({ type: 'none' });
      fetchPosts();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug: string) => {
    const token = await getToken({ template: 'baldydev' });
    if (!token) return;
    try {
      await api.posts.delete(slug, token);
      setDeleteConfirm(null);
      fetchPosts();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleTogglePublish = async (post: PostSummary) => {
    const token = await getToken({ template: 'baldydev' });
    if (!token) return;
    try {
      const full = await api.posts.get(post.slug, token);
      await api.posts.update(post.slug, { ...full, published: !post.published }, token);
      fetchPosts();
    } catch (err) {
      console.error('Toggle publish failed', err);
    }
  };

  // ── Auth gate ──────────────────────────────────────────────────────────────

  if (adminLoading) {
    return (
      <div className="admin-gate">
        <p className="loading">Checking permissions…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-gate">
        <p className="admin-gate-message">You don't have permission to view this page.</p>
        <button className="back-button" onClick={onNavigateHome}>
          Back to blog
        </button>
      </div>
    );
  }

  // ── Editor ─────────────────────────────────────────────────────────────────

  if (editor.type !== 'none') {
    return (
      <div className="admin-page">
        <div className="admin-toolbar">
          <button className="admin-back-btn" onClick={() => setEditor({ type: 'none' })}>
            ← All posts
          </button>
          <h1 className="admin-title">
            {editor.type === 'new' ? 'New post' : 'Edit post'}
          </h1>
          <div className="admin-toolbar-actions">
            <button
              className={`preview-toggle${preview ? ' active' : ''}`}
              onClick={() => setPreview((p) => !p)}
            >
              {preview ? 'Edit' : 'Preview'}
            </button>
            <label className="publish-toggle">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
              />
              Published
            </label>
            <button className="admin-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="editor-meta">
          <input
            className="editor-title-input"
            placeholder="Post title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="editor-meta-row">
            <select
              className="editor-select"
              value={form.kind}
              onChange={(e) =>
                setForm({ ...form, kind: e.target.value as 'article' | 'short' })
              }
            >
              <option value="article">Article</option>
              <option value="short">Short / Thought</option>
            </select>
            <input
              className="editor-input"
              placeholder="Author"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
            />
            <input
              className="editor-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <input
            className="editor-input editor-summary-input"
            placeholder="Summary / excerpt (optional — auto-generated from content if blank)"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
          />
        </div>

        {saveError && <p className="editor-error">{saveError}</p>}

        <div className="editor-body">
          {preview ? (
            <div className="editor-preview blog-content">
              <ReactMarkdown>{form.content}</ReactMarkdown>
            </div>
          ) : (
            <textarea
              className="editor-textarea"
              placeholder="Write in Markdown…"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              spellCheck
            />
          )}
        </div>
      </div>
    );
  }

  // ── Post list ──────────────────────────────────────────────────────────────

  return (
    <div className="admin-page">
      <div className="admin-toolbar">
        <button className="admin-back-btn" onClick={onNavigateHome}>
          ← Back to blog
        </button>
        <h1 className="admin-title">Admin</h1>
        <button className="admin-new-btn" onClick={openNew}>
          + New post
        </button>
      </div>

      {listLoading ? (
        <p className="loading">Loading posts…</p>
      ) : (
        <div className="admin-posts-list">
          {posts.length === 0 && (
            <p className="empty-state">No posts yet — create your first one!</p>
          )}
          {posts.map((post) => (
            <div key={post.slug} className="admin-post-row">
              <div className="admin-post-info">
                <span
                  className={`admin-post-status ${post.published ? 'status-live' : 'status-draft'}`}
                >
                  {post.published ? 'Live' : 'Draft'}
                </span>
                <span className="admin-post-kind">{post.kind}</span>
                <span className="admin-post-title">{post.title}</span>
                <span className="admin-post-date">
                  {(post.date || post.createdAt).slice(0, 10)}
                </span>
              </div>
              <div className="admin-post-actions">
                <button className="admin-action-btn" onClick={() => openEdit(post.slug)}>
                  Edit
                </button>
                <button
                  className="admin-action-btn"
                  onClick={() => handleTogglePublish(post)}
                >
                  {post.published ? 'Unpublish' : 'Publish'}
                </button>
                {deleteConfirm === post.slug ? (
                  <>
                    <button
                      className="admin-action-btn danger"
                      onClick={() => handleDelete(post.slug)}
                    >
                      Confirm delete
                    </button>
                    <button
                      className="admin-action-btn"
                      onClick={() => setDeleteConfirm(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="admin-action-btn danger"
                    onClick={() => setDeleteConfirm(post.slug)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
